import { Combobox, Label, makeStyles, Option, OptionOnSelectData, SelectionEvents } from '@fluentui/react-components';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHiddenComboboxCheckIconStyles } from '../../hooks/useHiddenComboboxCheckIconStyles';
import { ShowableTaxReturnItemRecord } from '../add-return/AddReturnPanel';
import { LISTBOX_MAX_HEIGHT } from '../common/VirtualizedOptionList';

export interface IEditReturnAdvancedOptionsSectionProps {
    taxReturnData: ShowableTaxReturnItemRecord | undefined;
    customGroups: any;
    onChangePostAmountGroup: (data: any) => void;
    onChangePostAmountOption: (data: ShowableTaxReturnItemRecord) => void;
    hasEditTaxReturnsDataEnabled?: boolean;
}

const useStyles = makeStyles({
    columnContainer: {
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
    },
    defaultLabelStyles: {
        lineHeight: 2,
    },
    combobox: {
        width: '312px',
        minWidth: '312px',
    },
    listbox: {
        maxHeight: `${LISTBOX_MAX_HEIGHT}px !important`,
        overflowY: 'auto',
    },
});

const EditReturnAdvancedOptionsSection: React.FC<IEditReturnAdvancedOptionsSectionProps> = ({
    taxReturnData,
    customGroups,
    onChangePostAmountGroup,
    onChangePostAmountOption,
    hasEditTaxReturnsDataEnabled = true,
}) => {
    const { t } = useTranslation();
    const { columnContainer, defaultLabelStyles, combobox, listbox } = useStyles();
    const { hidden: hiddenCheckIcon } = useHiddenComboboxCheckIconStyles();
    const [postAmountGroupFilter, setPostAmountGroupFilter] = useState('');

    const noneValue = 0;
    const postAmountAndCreateReportValue = 1;
    const createReportWithoutAmounts = 2;

    const postAmountOptionOptions = [
        {
            value: noneValue,
            text: t('none'),
        },
        {
            value: postAmountAndCreateReportValue,
            text: t('postAmountAndCreateReport'),
        },
        {
            value: createReportWithoutAmounts,
            text: t('createReportWithoutAmounts'),
        },
    ];

    const getPostAmountOption = (taxReturn: ShowableTaxReturnItemRecord | undefined) => {
        if (taxReturn?.updateBackReport) {
            if (taxReturn?.updateBack) {
                return postAmountAndCreateReportValue;
            }
            return createReportWithoutAmounts;
        }
        return noneValue;
    };

    const updateReturnBasedOnPostAmountOption = (taxReturn: ShowableTaxReturnItemRecord | undefined, postAmountOption: number): void => {
        let taxReturnCopy = { ...taxReturn };
        if (postAmountOption === noneValue) {
            taxReturnCopy = { ...taxReturn, updateBack: false, updateBackReport: false };
        } else if (postAmountOption === postAmountAndCreateReportValue) {
            taxReturnCopy = { ...taxReturn, updateBack: true, updateBackReport: true };
        } else {
            taxReturnCopy = { ...taxReturn, updateBack: false, updateBackReport: true };
        }

        setSelectedPostAmountOption(postAmountOption);
        onChangePostAmountOption(taxReturnCopy);
    };

    const [selectedPostAmountOption, setSelectedPostAmountOption] = React.useState(getPostAmountOption(taxReturnData));
    const selectedPostAmountText =
        postAmountOptionOptions.find((option) => option.value === selectedPostAmountOption)?.text?.toString() ?? '';

    const selectedPostAmountGroup = customGroups?.find(
        (group: any) => group.customGroupKey?.toString() === taxReturnData?.updateColumnFileKey?.toString()
    );

    const filteredPostAmountGroups =
        customGroups?.filter((group: any) => {
            const query = postAmountGroupFilter.toLowerCase();
            if (!query) {
                return true;
            }
            return (
                group.name?.toLowerCase().includes(query) ||
                group.description?.toLowerCase().includes(query) ||
                group.nameAndDescription?.toLowerCase().includes(query)
            );
        }) ?? [];

    const handlePostAmountOptionSelect = (_event: SelectionEvents, data: OptionOnSelectData) => {
        if (data.optionValue === undefined || data.optionValue === null) {
            return;
        }

        updateReturnBasedOnPostAmountOption(taxReturnData, parseInt(data.optionValue, 10));
    };

    const handlePostAmountGroupSelect = (_event: SelectionEvents, data: OptionOnSelectData) => {
        if (!data.optionValue) {
            onChangePostAmountGroup(undefined);
            setPostAmountGroupFilter('');
            return;
        }
        onChangePostAmountGroup(data.optionValue);
        setPostAmountGroupFilter('');
    };

    return (
        <>
            <div className={columnContainer}>
                <Label className={defaultLabelStyles}>{t('postAmountOption').toString()}</Label>
                <Combobox
                    id='postAmountOption'
                    data-testid='postAmountOption-input'
                    className={combobox}
                    value={selectedPostAmountText}
                    selectedOptions={[String(selectedPostAmountOption)]}
                    placeholder={t('postAmountOption').toString()}
                    disabled={!hasEditTaxReturnsDataEnabled}
                    onOptionSelect={handlePostAmountOptionSelect}
                    listbox={{ className: listbox }}
                >
                    {postAmountOptionOptions.map((option) => (
                        <Option key={option.value} className={hiddenCheckIcon} value={String(option.value)} text={option.text.toString()}>
                            {option.text}
                        </Option>
                    ))}
                </Combobox>
            </div>

            <div className={columnContainer}>
                <Label className={defaultLabelStyles}>{t('postAmountGroup').toString()}</Label>
                <Combobox
                    id='postAmountGroup'
                    className={combobox}
                    placeholder={t('selectPostAmountGroup').toString()}
                    value={postAmountGroupFilter || selectedPostAmountGroup?.nameAndDescription || selectedPostAmountGroup?.name || ''}
                    selectedOptions={taxReturnData?.updateColumnFileKey != null ? [String(taxReturnData.updateColumnFileKey)] : []}
                    onOptionSelect={handlePostAmountGroupSelect}
                    onChange={(event) => setPostAmountGroupFilter(event.target.value)}
                    freeform
                    clearable
                    disabled={!hasEditTaxReturnsDataEnabled}
                    listbox={{ className: listbox }}
                >
                    {filteredPostAmountGroups.length > 0 ? (
                        filteredPostAmountGroups.map((group: any) => (
                            <Option
                                key={String(group.customGroupKey)}
                                className={hiddenCheckIcon}
                                value={String(group.customGroupKey)}
                                text={group.nameAndDescription ?? group.name ?? ''}
                            >
                                {group.name}
                            </Option>
                        ))
                    ) : (
                        <Option
                            disabled
                            className={hiddenCheckIcon}
                            text={
                                postAmountGroupFilter
                                    ? t('noOptionsFound', { searchText: postAmountGroupFilter }).toString()
                                    : t('noRecordsToDisplay').toString()
                            }
                        >
                            {postAmountGroupFilter ? t('noOptionsFound', { searchText: postAmountGroupFilter }) : t('noRecordsToDisplay')}
                        </Option>
                    )}
                </Combobox>
            </div>
        </>
    );
};

export default EditReturnAdvancedOptionsSection;
